import {useTranslation} from '@/translations/i18n'
import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {EditDetailsSectionButtons} from '@/components/commons/edit-details-section-buttons/EditDetailsSectionButtons'
import {OtherDataCardInEditModeType} from '@/pages/project-details-other-data/ProjectDetailsOtherData'
import {ProjectExtended} from '../../types'
import {useGetProjectClinicalPresentations} from '../../queries/useGetProjectClinicalPresentations'
import {useOutletContext} from 'react-router-dom'
import {ClinicalPresentationsCardView} from '../clinical-presentations-view/ClinicalPresentationsCardView'
import {ClinicalPresentationsCardEmptyState} from '../clinical-presentations-view/ClinicalPresentationsCardEmptyState'
import {ErrorBox} from '@/components/commons/error-box/ErrorBox'
import {AlertCircleIcon} from '@/components/ui/icon/Icon'
import {ClinicalPresentationCardForm} from '../clinical-presentation-card-form/ClinicalPresentationCardForm'
import {useMemo} from 'react'
interface ClinicalPresentationsSectionProps {
    cardInEditMode: OtherDataCardInEditModeType
    setCardInEditMode: (cardInEditMode: OtherDataCardInEditModeType | null) => void
}

export const ClinicalPresentationsSection = ({
    cardInEditMode,
    setCardInEditMode
}: ClinicalPresentationsSectionProps) => {
    const {t} = useTranslation()
    const {project, isEditable} = useOutletContext<{project: ProjectExtended; isEditable: boolean}>()

    const {
        data: clinicalRecord,
        isLoading: isLoadingClinicalRecord,
        error: errorClinicalRecord,
        isError: isErrorClinicalRecord
    } = useGetProjectClinicalPresentations({
        urlParams: {projectId: project.id},
        options: {
            retry: false
        }
    })

    const handleCancel = () => {
        setCardInEditMode(null)
    }

    const handleEdit = () => {
        setCardInEditMode('clinical_presentations')
    }

    const isActive = cardInEditMode === 'clinical_presentations'

    const defaultValues = useMemo(() => {
        if (clinicalRecord) {
            return {
                ...clinicalRecord,
                cognitiveDisorders: clinicalRecord.cognitiveDisorders ?? undefined,
                behavioralDisorders: clinicalRecord.behavioralDisorders ?? undefined,
                communicationDisorders: clinicalRecord.communicationDisorders ?? undefined,
                sensoryDeficit: clinicalRecord.sensoryDeficit ?? undefined,
                stabilityLevel: clinicalRecord.stabilityLevel ?? undefined
            }
        }
        return {
            careNeeds: []
        }
    }, [clinicalRecord])

    if (isActive) {
        return (
            <ClinicalPresentationCardForm
                onCancel={handleCancel}
                isEdit={!!clinicalRecord}
                defaultValues={defaultValues}
            />
        )
    }

    return (
        <DetailsFieldsSection
            variant="neutral"
            title={t('projects:clinical_presentations')}
            isDisabled={cardInEditMode !== null}
            headingEndSlot={
                isEditable && (
                    <EditDetailsSectionButtons
                        isActive={isActive}
                        isDisabled={isLoadingClinicalRecord}
                        onCancel={handleCancel}
                        onEdit={handleEdit}
                    />
                )
            }
        >
            {isErrorClinicalRecord &&
            errorClinicalRecord?.response?.data.code !== 'project-clinical-presentation-not-found' ? (
                <ErrorBox icon={<AlertCircleIcon />} title={t('errors:default')} />
            ) : clinicalRecord ? (
                <ClinicalPresentationsCardView clinicalRecord={clinicalRecord} />
            ) : (
                <ClinicalPresentationsCardEmptyState isLoading={isLoadingClinicalRecord} />
            )}
        </DetailsFieldsSection>
    )
}
